import React, { useState } from 'react';
import useDispatchProgramService from '../../../app/services/hooks/useDispatchProgramService';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import { formatNumber } from '../../../utils/utils';
import useSweetAlert from '../../../hooks/useSweetAlert';
import { DispatchProgram } from '../../../app/models/DispatchProgram';

interface SplitRow {
    position: number;
    quantity: number;
    dispatch_date: string;
}

interface Props {
    dispatchProgramForm: DispatchProgram;
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: (message?: string) => void;
}

const WarehouseOrderItemSplit = ({ dispatchProgramForm, onSaved, onCancel, onError }: Props) => {
    const { storeSplitWarehouseOrderItem, fetchingStoreSplitWarehouseOrderItem } =
        useDispatchProgramService();

    const [splitData, setSplitData] = useState<SplitRow[]>([]);
    const [date, setDate] = useState<string>('');
    const [quantity, setQuantity] = useState<number>(0);
    const [errorFields, setErrorFields] = useState<any>();

    const woi = dispatchProgramForm.warehouse_order_item;

    const addRow = () => {
        const maxPos = splitData.length > 0 ? Math.max(...splitData.map((r) => r.position)) : 0;
        setSplitData([
            ...splitData,
            { position: maxPos + 1, quantity, dispatch_date: date }
        ]);
    };

    const removeRow = (position: number) => {
        setSplitData(splitData.filter((r) => r.position !== position));
    };

    const updateRow = (position: number, field: 'quantity' | 'dispatch_date', value: any) => {
        setSplitData(splitData.map((r) => (r.position === position ? { ...r, [field]: value } : r)));
    };

    const totalAdded = splitData.reduce((acc, r) => acc + Number(r.quantity), 0);

    const store = () => {
        if (splitData.length === 0) {
            toast.error('Agrega al menos una posición de despacho.');
            return;
        }
        useSweetAlert().spinnerAlert('Guardando posiciones de despacho...', 500);
        storeSplitWarehouseOrderItem(
            {
                dispatch_program_id: dispatchProgramForm.id,
                warehouse_order_item_id: dispatchProgramForm.warehouse_order_item_id,
                dispatch_program_item: splitData
            },
            {
                onSuccess: (response: ServiceResponse) => {
                    useSweetAlert().closeSpinnerAlert();
                    toast.success(response.message);
                    onSaved && onSaved();
                },
                onError: (response: ServiceResponse) => {
                    useSweetAlert().closeSpinnerAlert();
                    toast.error(response.message);
                    onError && onError(response.message);
                },
                onFieldError: (errorFields: ServiceResponse) => {
                    useSweetAlert().closeSpinnerAlert();
                    setErrorFields(errorFields.data);
                }
            }
        );
    };

    return (
        <>
            {/* Resumen de la posición */}
            <div className="alert alert-info mb-3">
                <div className="row">
                    {woi?.product_name && (
                        <div className="col-auto">
                            <strong>Producto:</strong> {woi.product_name}
                        </div>
                    )}
                    {woi?.service_name && (
                        <div className="col-auto">
                            <strong>Servicio:</strong> {woi.service_name}
                        </div>
                    )}
                    {woi?.depot_name && (
                        <div className="col-auto">
                            <strong>Almacén:</strong> {woi.depot_name}
                        </div>
                    )}
                    {woi?.quantity && (
                        <div className="col-auto">
                            <strong>Cant. pedido:</strong>{' '}
                            {formatNumber(parseFloat(woi.quantity))} {woi.quantity_unit ?? ''}
                        </div>
                    )}
                </div>
            </div>

            {/* Formulario para agregar filas */}
            <div className="row mb-3 align-items-end">
                <div className="col-md-5">
                    <label className="form-label">Fecha de despacho</label>
                    <input
                        type="datetime-local"
                        className="form-control form-control-sm"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                    />
                </div>
                <div className="col-md-4">
                    <label className="form-label">Cantidad ({woi?.quantity_unit ?? ''})</label>
                    <input
                        type="number"
                        className="form-control form-control-sm"
                        value={quantity}
                        min={0}
                        onChange={(e) => setQuantity(Number(e.target.value))}
                    />
                </div>
                <div className="col-md-3">
                    <button
                        className="btn btn-success btn-sm w-100"
                        onClick={addRow}
                        disabled={!date || quantity <= 0}
                    >
                        <i className="mdi mdi-plus" /> Agregar
                    </button>
                </div>
            </div>

            {/* Tabla de filas agregadas */}
            {splitData.length > 0 && (
                <table className="table table-sm table-bordered mb-3">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Fecha de despacho</th>
                            <th>Cantidad ({woi?.quantity_unit ?? ''})</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {splitData.map((row) => (
                            <tr key={row.position}>
                                <td>{row.position}</td>
                                <td>
                                    <input
                                        type="datetime-local"
                                        className="form-control form-control-sm"
                                        value={row.dispatch_date}
                                        onChange={(e) =>
                                            updateRow(row.position, 'dispatch_date', e.target.value)
                                        }
                                    />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        value={row.quantity}
                                        min={0}
                                        onChange={(e) =>
                                            updateRow(row.position, 'quantity', Number(e.target.value))
                                        }
                                    />
                                </td>
                                <td>
                                    <button
                                        className="btn btn-danger btn-sm"
                                        onClick={() => removeRow(row.position)}
                                    >
                                        <i className="mdi mdi-delete" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={2} className="text-end fw-bold">
                                Total agregado:
                            </td>
                            <td className="fw-bold">
                                {formatNumber(totalAdded)} {woi?.quantity_unit ?? ''}
                            </td>
                            <td></td>
                        </tr>
                    </tfoot>
                </table>
            )}

            <hr />
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm
                        callbackCancel={() => onCancel && onCancel()}
                        locked={fetchingStoreSplitWarehouseOrderItem}
                    />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm
                        callbackSave={store}
                        locked={fetchingStoreSplitWarehouseOrderItem}
                    />
                </div>
            </div>
        </>
    );
};

export default WarehouseOrderItemSplit;
